/**
 * TypeAwareInput.tsx - Type-Aware Input Component
 * 
 * CHANGE HISTORY:
 * - 251128_1308 danielbeer: Created type-aware input for field editing
 * 
 * Features:
 * - Automatic input type based on field type
 * - String: text input
 * - Number: number input
 * - Boolean: checkbox
 * - Date: date input
 * - Select: dropdown
 * - Text: textarea
 * - JSON: code editor (textarea with monospace)
 */

import React from 'react';
import type { FieldType } from '../types/index';
import './TypeAwareInput.css';

export interface TypeAwareInputProps {
  type: FieldType;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  options?: string[];  // For select type
  disabled?: boolean;
  className?: string;
}

/**
 * TypeAwareInput - Renders appropriate input based on field type
 */
export const TypeAwareInput: React.FC<TypeAwareInputProps> = ({
  type,
  value,
  onChange,
  placeholder,
  options = [],
  disabled = false,
  className = ''
}) => {
  const baseClass = `type-aware-input ${className}`;

  switch (type) {
    case 'string':
      return (
        <input
          type="text"
          className={`${baseClass} type-aware-input--text`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={`${baseClass} type-aware-input--number`}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          placeholder={placeholder}
          disabled={disabled}
        />
      );

    case 'boolean':
      return (
        <label className={`${baseClass} type-aware-input--boolean`}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <span className="type-aware-input__checkbox-label">
            {value ? 'True' : 'False'}
          </span>
        </label>
      );

    case 'date':
      return (
        <input
          type="date"
          className={`${baseClass} type-aware-input--date`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <select
          className={`${baseClass} type-aware-input--select`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">{placeholder || 'Select...'}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );

    case 'text':
      return (
        <textarea
          className={`${baseClass} type-aware-input--textarea`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={4}
        />
      );

    case 'json':
      return (
        <textarea
          className={`${baseClass} type-aware-input--json`}
          value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange(parsed);
            } catch {
              // Keep as string if invalid JSON
              onChange(e.target.value);
            }
          }}
          placeholder={placeholder || '{}'}
          disabled={disabled}
          rows={6}
          spellCheck={false}
        />
      );

    case 'transform':
      return (
        <textarea
          className={`${baseClass} type-aware-input--transform`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Enter transformation expression...'}
          disabled={disabled}
          rows={3}
          spellCheck={false}
        />
      );

    default:
      // Fallback to text input
      return (
        <input
          type="text"
          className={`${baseClass} type-aware-input--text`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
      );
  }
};
